import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, SxProps, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DateFormatsEnum } from '../../constants/date';
import useCopyClipboard from '../../hooks/useCopyClipboard';
import {
  selectCurrentOrganizationDepartmentList,
  selectIsProductMarketplaceEnabled,
} from '../../store/selectors/organizations';
import { IReport } from '../../types/reports';
import { formatZonedDateTime, isValidDate, resolveStoredTimezone } from '../../utils/date';
import { getFullName } from '../../utils/helpers';
import {
  calculateDaysLeft,
  getReportGiftBudget,
  getReportGiftBudgetUsage,
  getReportItemSources,
  getReportStatusAndDate,
  isCampaignPicked,
  isValidScheduledEngagement,
} from '../../utils/reports';
import { getDepartmentLabelById } from '../../utils/users';
import { ReportsStatus } from '../index';
import ReportCampaignName from '../ReportsComponent/components/ReportCampaignName';
import ReportGiftBudget from '../ReportsComponent/components/ReportGiftBudget';
import ReportItemSource from '../ReportsComponent/components/ReportItemSource';
import { PersonCard } from './index';

const flexColumn = {
  display: 'flex',
  flexDirection: 'column',
};

const flexColumnWithGap = {
  ...flexColumn,
  gap: 1,
};

const flexCenteredWithGap = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

interface IProps {
  className?: string;
  engagement: IReport;
  controls?: React.ReactNode | React.ReactNode[];
  wide?: boolean;
}

const EngagementCard = ({ engagement, controls }: IProps) => {
  const departments = useSelector(selectCurrentOrganizationDepartmentList);
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);
  const { breakpoints, palette, shadows, typography } = useTheme();

  const ellipsisContainer = {
    width: '100%',
    display: 'block',
    whiteSpace: 'nowrap',
    textAlign: 'start',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 250,
    cursor: 'pointer',
    [breakpoints.between(1200, 1440)]: {
      maxWidth: 150,
    },
  };

  const controlsStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '20px',
    position: 'absolute',
    bottom: 24,
    right: 24,

    [breakpoints.between(1200, 1440)]: {
      bottom: 16,
      right: 16,
    },

    [breakpoints.down(786)]: {
      position: 'relative',
      top: 5,
      left: 0,
    },
  };

  const ellipsisResponsiveStyle = {
    ...ellipsisContainer,
    [breakpoints.between(1200, 1470)]: {
      maxWidth: 120,
    },
  };

  const containerStyles = {
    position: 'relative',
    boxShadow: '1px 1px 10px rgba(0, 0, 0, 0.05)',
    background: palette.common.white,
    borderRadius: 0.5,
    maxWidth: '100%',
    padding: 3,
    height: '362px',
    transition: 'box-shadow 0.3s ease-in-out',

    [breakpoints.between(1200, 1439)]: {
      padding: 2,
    },

    '@media (max-width:786px)': {
      height: '100%',
    },

    '&:hover': {
      boxShadow: shadows[4],
    },
  };

  const BottomContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 3,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  }));

  const OrderIdCopyButton = styled(Typography)(() => ({
    cursor: 'pointer',
    borderRight: `1px solid ${palette.divider}`,
    paddingRight: 12,
    fontWeight: typography.fontWeightMedium,
    '&:hover': {
      textDecoration: 'underline',
    },
  }));

  const TopContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  }));

  const OrderInfoContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    width: '50%',
  }));

  const StatusContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
  }));

  const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000 });

  const {
    order_id: orderId,
    campaign_name,
    pre_created_engagement_id,
    uid,
    delayed_shipping_expiration,
    sender_first_name,
    sender_last_name,
    sender_email,
    department_id,
    receiver_email,
    receiver_last_name,
    receiver_first_name,
    receiver_company_name,
    created_by,
    created_at,
  } = useMemo(() => engagement, [engagement]);

  const isScheduledEngagement = useMemo(() => isValidScheduledEngagement(engagement), [engagement]);
  const { label: status, color: backgroundColor } = useMemo(() => getReportStatusAndDate(engagement), [engagement]);
  const isPYGChipShown = useMemo(() => !isCampaignPicked(engagement), [engagement]);

  const getDepartmentLabel = useCallback((id: string) => getDepartmentLabelById(id, departments), [departments]);

  const isExpDateValid = useMemo(() => {
    return delayed_shipping_expiration && isValidDate(new Date(delayed_shipping_expiration));
  }, [delayed_shipping_expiration]);

  const createdDate = useMemo(() => format(new Date(created_at), DateFormatsEnum.ddMMMyyyy), [created_at]);
  const expirationDate = useMemo(
    () =>
      formatZonedDateTime(
        new Date(delayed_shipping_expiration),
        resolveStoredTimezone(engagement.timezone_metadata, 'delayed_shipping_expiration'),
      ),
    [delayed_shipping_expiration, engagement.timezone_metadata],
  );

  const daysLeftToExpirationDate = useMemo(
    () => calculateDaysLeft(delayed_shipping_expiration),
    [delayed_shipping_expiration],
  );

  const itemSources = useMemo(() => getReportItemSources(engagement), [engagement]);
  const giftBudget = useMemo(() => getReportGiftBudget(engagement), [engagement]);
  const giftBudgetUsage = useMemo(() => getReportGiftBudgetUsage(engagement), [engagement]);
  const areMarketplaceFieldsShown = useMemo(
    () => isMarketplaceEnabled && !isScheduledEngagement,
    [isMarketplaceEnabled, isScheduledEngagement],
  );

  const renderTopContainer = useCallback(() => {
    return (
      <TopContainer>
        <OrderInfoContainer>
          <Box sx={{ flexShrink: 0 }}>
            <OrderIdCopyButton onClick={() => setCopied(orderId, 'Order ID')}>{orderId}</OrderIdCopyButton>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <ReportCampaignName
              preCreatedEngagementId={pre_created_engagement_id}
              campaignName={campaign_name}
              isPYGChipShown={isPYGChipShown}
              textSx={ellipsisResponsiveStyle}
            />
          </Box>
        </OrderInfoContainer>
        <StatusContainer>
          {!isScheduledEngagement && <ReportsStatus ellipsis status={status} backgroundColor={backgroundColor} />}
        </StatusContainer>
      </TopContainer>
    );
  }, [
    orderId,
    setCopied,
    pre_created_engagement_id,
    campaign_name,
    isPYGChipShown,
    isScheduledEngagement,
    status,
    backgroundColor,
  ]);

  const renderCreatedBy = useCallback(() => {
    return (
      <Box sx={{ ...flexCenteredWithGap, flexWrap: 'wrap' }}>
        <Typography color="text.secondary">Created by:</Typography>
        <Typography title={created_by} sx={{ ...ellipsisContainer }}>
          {created_by}
        </Typography>
      </Box>
    );
  }, [created_by]);

  const renderItemSource = useCallback(() => {
    return (
      <Box sx={{ ...flexCenteredWithGap }}>
        <Typography color="text.secondary">Item source:</Typography>
        <ReportItemSource sources={itemSources} />
      </Box>
    );
  }, [itemSources]);

  const renderGiftBudget = useCallback(() => {
    return (
      <Box sx={{ ...flexCenteredWithGap }}>
        <Typography color="text.secondary">Gift budget:</Typography>
        <ReportGiftBudget budget={giftBudget} usage={giftBudgetUsage} inline />
      </Box>
    );
  }, [giftBudget, giftBudgetUsage]);

  const renderCreatedDate = useCallback(() => {
    return (
      <Box sx={{ ...flexCenteredWithGap }}>
        <Typography color="text.secondary">Created:</Typography>
        <Typography>{createdDate}</Typography>
      </Box>
    );
  }, [createdDate]);

  const renderExpirationDate = useCallback(() => {
    return (
      <Box sx={{ ...flexCenteredWithGap }}>
        <Typography color="text.secondary" data-for={`date-exp-tooltip-${uid}`}>
          Expires:
        </Typography>
        <Tooltip title={daysLeftToExpirationDate} arrow placement="top">
          <Typography> {expirationDate}</Typography>
        </Tooltip>
      </Box>
    );
  }, [delayed_shipping_expiration, uid]);

  const renderPeopleInfo = useCallback(() => {
    const fullSenderName = getFullName(sender_first_name, sender_last_name);
    const fullReceiverName = getFullName(receiver_first_name, receiver_last_name);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
          },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <PersonCard
          label="Sender"
          top={
            <Typography sx={{ ...ellipsisContainer, minWidth: 0 }} title={fullSenderName}>
              {fullSenderName}
            </Typography>
          }
          middle={
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Typography color="text.secondary" sx={{ ...ellipsisContainer }} title={sender_email}>
                {sender_email}
              </Typography>
            </Box>
          }
          bottom={<Typography color="text.secondary">{getDepartmentLabel(department_id)}</Typography>}
        />
        <ArrowForwardIcon
          color="action"
          sx={{
            '@media (max-width: 768px)': {
              transform: 'rotate(90deg)',
            },
            my: '10px',
            mx: 0,
          }}
        />

        <PersonCard
          label="Receiver"
          top={
            <Typography sx={{ ...ellipsisContainer, minWidth: 0 }} title={fullReceiverName}>
              {fullReceiverName}
            </Typography>
          }
          middle={
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Typography color="text.secondary" sx={{ ...ellipsisContainer }} title={receiver_email}>
                {receiver_email}
              </Typography>
            </Box>
          }
          bottom={<Typography color="text.secondary">{getDepartmentLabel(receiver_company_name)}</Typography>}
        />
      </Box>
    );
  }, [
    sender_first_name,
    sender_last_name,
    sender_email,
    department_id,
    receiver_first_name,
    receiver_last_name,
    receiver_email,
    receiver_company_name,
    getDepartmentLabel,
  ]);

  return (
    <Box sx={{ ...containerStyles }}>
      {renderTopContainer()}
      <Box
        sx={{
          marginTop: 3,
          ...flexColumn,
          flex: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {renderPeopleInfo()}
          <Box sx={{ marginTop: 2 }}>
            <BottomContainer>
              <Box sx={{ ...flexColumnWithGap, flex: 1 }}>
                {renderCreatedBy()}
                {areMarketplaceFieldsShown && renderItemSource()}
                {areMarketplaceFieldsShown && !!giftBudget && renderGiftBudget()}
              </Box>
              <Box sx={{ ...flexColumnWithGap, flexShrink: 0 }}>
                {!isScheduledEngagement && renderCreatedDate()}
                {isExpDateValid && renderExpirationDate()}
              </Box>
            </BottomContainer>
          </Box>
        </Box>
        <Box sx={controlsStyle}>{controls}</Box>
      </Box>
    </Box>
  );
};

export default EngagementCard;
